import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Tag, AlertCircle } from 'lucide-react';
import { brandService } from '../services/brandService';
import { BrandRequestDTO, BrandResponseDTO, BrandFormData, BrandFormErrors } from '../types/brand';
import { alertService } from '../services/alertService';

const BrandInfoPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = !!id;

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(isEditing);
  const [errors, setErrors] = useState<BrandFormErrors>({});
  
  const [formData, setFormData] = useState<BrandFormData>({
    nome: '',
    descricao: '',
    ativo: true
  });

  // Carregar dados da marca para edição
  useEffect(() => {
    if (isEditing && id) {
      loadBrandData(id);
    }
  }, [isEditing, id]);

  const loadBrandData = async (brandId: string) => {
    try {
      setLoadingData(true);
      const response = await brandService.getById(brandId);
      
      if (response.data) {
        const brand = response.data;
        setFormData({
          nome: brand.nome,
          descricao: brand.descricao || '',
          ativo: brand.ativo
        });
      } else {
        alertService.error('Erro ao carregar dados da marca');
        navigate('/marcas');
      }
    } catch (error) {
      console.error('Erro ao carregar marca:', error);
      alertService.error('Erro ao carregar dados da marca');
      navigate('/marcas');
    } finally {
      setLoadingData(false);
    }
  };

  // Validar formulário
  const validateForm = (): boolean => {
    const newErrors: BrandFormErrors = {};

    if (!formData.nome.trim()) {
      newErrors.nome = 'Nome é obrigatório';
    } else if (formData.nome.trim().length < 2) {
      newErrors.nome = 'Nome deve ter pelo menos 2 caracteres';
    } else if (formData.nome.trim().length > 100) {
      newErrors.nome = 'Nome deve ter no máximo 100 caracteres';
    }

    if (formData.descricao && formData.descricao.length > 500) {
      newErrors.descricao = 'Descrição deve ter no máximo 500 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Verificar se nome já existe
  const checkNameExists = async (nome: string): Promise<boolean> => {
    try {
      const response = await brandService.checkNameExists(nome, isEditing ? id : undefined);
      return response.data || false;
    } catch (error) {
      console.error('Erro ao verificar nome:', error);
      return false;
    }
  };

  // Salvar marca
  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    // Verificar se nome já existe
    const nameExists = await checkNameExists(formData.nome.trim());
    if (nameExists) {
      setErrors({ nome: 'Já existe uma marca com este nome' });
      return;
    }

    try {
      setLoading(true);

      const requestData: BrandRequestDTO = {
        nome: formData.nome.trim(),
        descricao: formData.descricao.trim() || undefined,
        ativo: formData.ativo
      };

      if (isEditing && id) {
        await brandService.update(id, requestData);
        alertService.success('Marca atualizada com sucesso');
      } else {
        await brandService.create(requestData);
        alertService.success('Marca criada com sucesso');
      }
      
      navigate('/marcas');
    } catch (error) {
      console.error('Erro ao salvar marca:', error);
      alertService.error(
        isEditing ? 'Erro ao atualizar marca' : 'Erro ao criar marca'
      );
    } finally {
      setLoading(false);
    }
  };

  // Atualizar campo do formulário
  const updateField = (field: keyof BrandFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[field as keyof BrandFormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  if (loadingData) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Carregando...</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/marcas')}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-red-100 rounded-lg">
            <Tag className="text-red-600" size={24} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isEditing ? 'Editar Marca' : 'Nova Marca'}
            </h1>
            <p className="text-gray-600">
              {isEditing ? 'Atualize as informações da marca' : 'Preencha as informações da nova marca'}
            </p>
          </div>
        </div>
      </div>

      {/* Formulário */}
      <div className="max-w-2xl">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="space-y-6">
            {/* Nome */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nome *
              </label>
              <input
                type="text"
                value={formData.nome}
                onChange={(e) => updateField('nome', e.target.value)}
                placeholder="Digite o nome da marca"
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${
                  errors.nome ? 'border-red-500' : 'border-gray-300'
                }`}
                maxLength={100}
              />
              {errors.nome && (
                <div className="flex items-center gap-1 mt-1 text-red-600 text-sm">
                  <AlertCircle size={16} />
                  {errors.nome}
                </div>
              )}
            </div>

            {/* Descrição */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Descrição
              </label>
              <textarea
                value={formData.descricao}
                onChange={(e) => updateField('descricao', e.target.value)}
                placeholder="Digite uma descrição para a marca (opcional)"
                rows={3}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${
                  errors.descricao ? 'border-red-500' : 'border-gray-300'
                }`}
                maxLength={500}
              />
              {errors.descricao && (
                <div className="flex items-center gap-1 mt-1 text-red-600 text-sm">
                  <AlertCircle size={16} />
                  {errors.descricao}
                </div>
              )}
              <div className="text-sm text-gray-500 mt-1">
                {formData.descricao.length}/500 caracteres
              </div>
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="ativo"
                    checked={formData.ativo}
                    onChange={() => updateField('ativo', true)}
                    className="text-red-600 focus:ring-red-500"
                  />
                  <span className="text-sm text-gray-700">Ativo</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="ativo"
                    checked={!formData.ativo}
                    onChange={() => updateField('ativo', false)}
                    className="text-red-600 focus:ring-red-500"
                  />
                  <span className="text-sm text-gray-700">Inativo</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Botões */}
        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={() => navigate('/marcas')}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Save size={16} />
            {loading ? 'Salvando...' : isEditing ? 'Atualizar Marca' : 'Criar Marca'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BrandInfoPage;